import React, {useEffect, useRef} from "react";
import {GLCanvas} from "../controllers/GLCanvas";
import {Shader} from "../controllers/Shader";
import {Matrix} from "../controllers/Matrix";
import {Polygon} from "../controllers/Polygon";
import {GLObject} from "../controllers/GLObject";
import {GLModel} from "../controllers/GLModel";
import {Camera} from "../controllers/Camera";
import {Tube} from "../controllers/Tube";
import {GLInstancedObject} from "../controllers/GLInstancedObject";
import {GPUTube} from "../controllers/GPUTube";

interface BGCanvasProps{
    currentSection:number;
}



const BGCanvas:React.FC<BGCanvasProps> = (props) => {

    function isSmartPhone() {
        if (navigator.userAgent.match(/iPhone|Android.+Mobile/)) {
            return true;
        } else {
            return false;
        }
    }

    const currentSectionRef = useRef(0.001);
    useEffect(() => {
        currentSectionRef.current = props.currentSection;
    },[props.currentSection]);
    // @ts-ignore
    const canvasRef = useRef(null as HTMLCanvasElement);
    useEffect(() => {
        if(canvasRef.current == null){
            return;
        }

        const isPhone = isSmartPhone();
        console.log("isPhone:" + isPhone);
        const canvas = canvasRef.current;
        canvas.height = canvas.clientHeight;
        canvas.width = canvas.clientWidth;
        const gl = canvas.getContext("webgl")as WebGLRenderingContext;
        const ext = gl.getExtension('ANGLE_instanced_arrays');
        if(ext == null){
            alert('ANGLE_instanced_arrays not supported');
            return;
        }else{
            console.log(ext);
        }
        console.log(gl);
        GLCanvas.Clear(gl);
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        const red = [250,0,60];
        const blue = [0,183,250];
        const shadow = [75,75,75];
        const white = [255,255,255];
        const shader = Shader.CreateShader(Shader.vertex,Shader.UniformColorFrag(),gl) as WebGLProgram;
        const instancingShader = Shader.CreateShader(Shader.SimpleInstanceVertex(),Shader.UniformColorFrag(),gl) as WebGLProgram;
        const tubeShader = Shader.CreateShader(Shader.TubeInstanceVertex(),Shader.UniformColorFrag(),gl) as WebGLProgram;

        const donut_M = Polygon.GenDonut(0.1,0.15,32);
        const cross_M = Polygon.GenCross(0.1,0.025);
        const gpuTube_M = Polygon.GenTube(0.0125,8);
        const _tube = new Tube();
        const particleBound = isPhone ? 15:30;
        //_tube.trajectory = (t:number) => {return[2.0*Math.cos(t)*Math.sin(t)+Math.cos(t*10.0),2.0*Math.sin(t),+Math.sin(t*10.0)];}
        const tube_M = _tube.GenerateVerticesAndIndices(1.0,0.025,12,314);
        const cameraPos = [[0.0,0.0,3.0],[0.0,-3.0,-1.0],[3.0,-2.0,-0.75],[3.0,-2.0,-0.75],[3.0,-3.0,3.0],[-2.0,0.0,-6.0]];
        const lookPos = [[0.0,0.0,0.0],[0.0,-5.0,-3.0],[0.0,-5.0,-3.0],[0.0,-5.0,-3.0],[0.0,-5.0,-3.0],[0.0,-5.0,-3.0]];

        const donuts:Array<GLObject> = [];
        const donutsRotSpeed:Array<number> = [];
        const tubes:Array<GLObject> = [];
        const tubeOffset:Array<number> = [];
        const tubePosOffset:Array<number> = [];
        const tubeTimeOffset:Array<number> = [];
        const instanceCount = isPhone ? 128 : 512;
        const tubeCount = 10;
        const tubeDiv = 64;
        const instancedDonuts = new GLInstancedObject(instancingShader,new GLModel(donut_M[0],donut_M[1],gl),gl,instanceCount);
        const instancedCrosses = new GLInstancedObject(instancingShader,new GLModel(cross_M[0],cross_M[1],gl),gl,instanceCount);
        const instancedDonutsShadow = new GLInstancedObject(instancingShader,new GLModel(donut_M[0],donut_M[1],gl),gl,instanceCount);
        const instancedCrossesShadow = new GLInstancedObject(instancingShader,new GLModel(cross_M[0],cross_M[1],gl),gl,instanceCount);
        const instancedTubeA = new GPUTube(tubeShader,new GLModel(gpuTube_M[0],gpuTube_M[1],gl),gl,tubeDiv,tubeCount);
        const instancedTubeB = new GPUTube(tubeShader,new GLModel(gpuTube_M[0],gpuTube_M[1],gl),gl,tubeDiv,tubeCount);
        const instancedTubeC = new GPUTube(tubeShader,new GLModel(gpuTube_M[0],gpuTube_M[1],gl),gl,tubeDiv,tubeCount);

        instancedDonuts.uniforms.push({name:"color",val:blue});
        instancedCrosses.uniforms.push({name:"color",val:red});
        instancedTubeA.uniforms.push({name:"color",val:red});
        instancedTubeA.uniforms.push({name:"radius",val:[0.0,0.0,0.0]});
        instancedTubeB.uniforms.push({name:"color",val:shadow});
        instancedTubeB.uniforms.push({name:"radius",val:[0.0,0.0,0.0]});
        instancedTubeC.uniforms.push({name:"color",val:blue});
        instancedTubeC.uniforms.push({name:"radius",val:[0.0,0.0,0.0]});
        instancedDonutsShadow.uniforms.push({name:"color",val:shadow});
        instancedCrossesShadow.uniforms.push({name:"color",val:shadow});
        for(let i = 0; i < 0.0;i++){
            let donut:GLObject;
            let donut2:GLObject;
            if(Math.random() > 0.5){
                donut = new GLObject(shader,new GLModel(donut_M[0],donut_M[1],gl));
                donut.uniforms.push({name:"color",val:blue});
                donut2 = new GLObject(shader,new GLModel(donut_M[0],donut_M[1],gl));
                donut2.uniforms.push({name:"color",val:shadow});
            }else{
                donut = new GLObject(shader,new GLModel(cross_M[0],cross_M[1],gl));
                donut.uniforms.push({name:"color",val:red});
                donut2 = new GLObject(shader,new GLModel(cross_M[0],cross_M[1],gl));
                donut2.uniforms.push({name:"color",val:shadow});
            }
            donut.Translate([Math.random()*particleBound-particleBound*0.5,Math.random()*particleBound-particleBound*0.5,Math.random()*2.0]);
            donut2.Translate([donut.position[0],donut.position[1],0.0]);
            donut2.Translate([0.025,0.025,-0.001]);
            const rotSpeed = Math.random() * 0.025 + 0.075;
            donutsRotSpeed.push(rotSpeed);
            donutsRotSpeed.push(rotSpeed);
            donuts.push(donut);
            donuts.push(donut2);
        }

        for(let i = 0; i < instanceCount;i++){
            var instanceDonutsPos = [Math.random()*particleBound-particleBound*0.5,Math.random()*particleBound-particleBound*0.5,Math.random()*1.0];
            instancedDonuts.Translate(instanceDonutsPos,i);
            var instanceCrossPos = [Math.random()*particleBound-particleBound*0.5,Math.random()*particleBound-particleBound*0.5,Math.random()*1.0];
            instancedCrosses.Translate(instanceCrossPos,i);
            instanceDonutsPos[2] = 0;
            instanceCrossPos[2] = 0;
            instancedDonutsShadow.Translate(instanceDonutsPos,i);
            instancedCrossesShadow.Translate(instanceCrossPos,i);
        }
        instancedDonuts.UpdateBuffer(gl);
        instancedCrosses.UpdateBuffer(gl);
        instancedDonutsShadow.UpdateBuffer(gl);
        instancedCrossesShadow.UpdateBuffer(gl);
        for(let i = 0; i <0.0; i++){
            // @ts-ignore
            let tube:GLObject = null;
            switch (i % 3){
                case 0:
                    tube = new GLObject(shader,new GLModel(tube_M[0],tube_M[1],gl));
                    tube.uniforms.push({name:"color",val:shadow});
                    break;
                case 1:
                    tube = new GLObject(shader,new GLModel(tube_M[0],tube_M[1],gl));
                    tube.uniforms.push({name:"color",val:blue});
                    break;
                case 2:
                    tube = new GLObject(shader,new GLModel(tube_M[0],tube_M[1],gl));
                    tube.uniforms.push({name:"color",val:red});
                    break;
            }
            tube.Translate([Math.random()*0.5,-5.0+Math.random()*0.5,-3.0+Math.random()*0.5]);
            tubeOffset.push(Math.random()*0.25);
            tubes.push(tube);
        }
        for(let i = 0; i < tubeCount*3; i++){
            tubeTimeOffset.push(Math.random()*0.25);
            tubePosOffset.push(Math.random()*0.5,-5.0+Math.random()*0.5,-3.0+Math.random()*0.5);

        }
        const camera = new Camera(canvas);
        camera.LookAt([0.0,0.0,2.0],[0.0,0.0,0.0],[-1.0,1.0,0.0]);
        let t = 0.0;
        let r = 0.0;
        let sectionTransition = 0.01;

        const func = (from:number,to:number,trans:number,t:number) => {
            const a0 = [0.5*Math.cos(t*3.0),0.5*Math.sin(t*3.0),0.0];
            const a1 = [2.0*Math.cos(t*3.0),2.0*Math.sin(t*3.0),0.25*Math.cos(t)];
            const a2 = [4.0*Math.cos(t)*Math.sin(t)+2.0*Math.cos(t*10.0),4.0*Math.sin(t),2.0*Math.sin(t*10.0)];
            const a3 = [4.0*Math.cos(t)*Math.sin(t)+2.0*Math.cos(t*10.0),4.0*Math.sin(t),2.0*Math.sin(t*10.0)];
            const a4 = [4.0*Math.cos(t*5.0)*Math.sin(t*2.5),2.0*Math.sin(t*1.0),4.0*Math.sin(t*5.0)];
            const a5 = [4.0*Math.cos(t)*Math.sin(t)+2.0*Math.cos(t*10.0),4.0*Math.sin(t),2.0*Math.sin(t*10.0)];
            const a = [a0,a1,a2,a3,a4,a5];
            return [a[from][0]*(1-trans) + a[to][0]*trans,a[from][1]*(1-trans) + a[to][1]*trans,a[from][2]*(1-trans) + a[to][2]*trans];
        }

        const render = () => {
            GLCanvas.Clear(gl);
            t += 0.01;
            sectionTransition = (currentSectionRef.current) * 0.02 + sectionTransition * 0.98;
            const from = (currentSectionRef.current + Math.sign(sectionTransition-currentSectionRef.current));
            const to = (currentSectionRef.current);
            const trans = (sectionTransition - from)/(to - from);
            const c = [cameraPos[to][0] * trans + cameraPos[from][0] * (1-trans),
                cameraPos[to][1] * trans + cameraPos[from][1] * (1-trans),
                cameraPos[to][2] * trans + cameraPos[from][2] * (1-trans)]
            const l = [lookPos[to][0] * trans + lookPos[from][0] * (1-trans),
                lookPos[to][1] * trans + lookPos[from][1] * (1-trans),
                lookPos[to][2] * trans + lookPos[from][2] * (1-trans)]
            camera.LookAt(c,l,[-1.0,1.0,0.0]);
            for(let i = 0; i < donuts.length;i++){
                donuts[i].Translate([0.0,0.01,0.0]);
                donuts[i].Rotate([0.0,0.0,donutsRotSpeed[i]]);
                donuts[i].position[1] = (donuts[i].position[1]+particleBound * 0.5)%particleBound-particleBound *0.5;
                //donuts[i].Draw(camera,gl);
                if(i % 2 == 1){
                    if(currentSectionRef.current == 4){
                        donuts[i].uniforms[0].val = white;
                    }else{
                        donuts[i].uniforms[0].val = shadow;
                    }
                }
            }
            for(let i = 0; i < instanceCount;i++){

                instancedDonuts.Translate([0.0,0.01,0.0],i);
                instancedDonuts.Rotate([0.0,0.00,0.01],i);
                instancedDonuts.instancePositions[(i * 3) + 1] = (instancedDonuts.instancePositions[(i * 3) + 1]+particleBound*0.5)%particleBound-particleBound * 0.5;
                instancedCrosses.Translate([0.0,0.01,0.0],i);
                instancedCrosses.Rotate([0.0,0.00,0.01],i);
                instancedCrosses.instancePositions[(i * 3) + 1] = (instancedCrosses.instancePositions[(i * 3) + 1]+particleBound*0.5)%particleBound-particleBound * 0.5;
                instancedDonutsShadow.Translate([0.0,0.01,0.0],i);
                instancedDonutsShadow.Rotate([0.0,0.00,0.01],i);
                instancedDonutsShadow.instancePositions[(i * 3) + 1] = (instancedDonutsShadow.instancePositions[(i * 3) + 1]+particleBound*0.5)%particleBound-particleBound * 0.5;
                instancedCrossesShadow.Translate([0.0,0.01,0.0],i);
                instancedCrossesShadow.Rotate([0.0,0.00,0.01],i);
                instancedCrossesShadow.instancePositions[(i * 3) + 1] = (instancedCrossesShadow.instancePositions[(i * 3) + 1]+particleBound*0.5)%particleBound-particleBound * 0.5;
            }
            if(currentSectionRef.current == 4){
                instancedDonutsShadow.uniforms[0].val = white;
                instancedCrossesShadow.uniforms[0].val = white;
                instancedTubeB.uniforms[0].val = white;
            }else{
                instancedDonutsShadow.uniforms[0].val = shadow;
                instancedCrossesShadow.uniforms[0].val = shadow;
                instancedTubeB.uniforms[0].val = shadow;
            }
            instancedDonuts.UpdateBuffer(gl);
            instancedCrosses.UpdateBuffer(gl);
            instancedDonutsShadow.UpdateBuffer(gl);
            instancedCrossesShadow.UpdateBuffer(gl);
            for(let i = 0; i < tubes.length; i++){
                const tubeData = _tube.GenerateVerticesAndIndices(1.0+t+tubeOffset[i],r,12,100);
                tubes[i].model.Update(tubeData[0],tubeData[1],gl);
                if(i % 3 == 0){
                    if(currentSectionRef.current == 4){
                        tubes[i].uniforms[0].val = white;
                    }else{
                        tubes[i].uniforms[0].val = shadow;
                    }
                }
                //tubes[i].Draw(camera,gl);
            }
            r = Math.min(sectionTransition,1.0);
            // switch (currentSectionRef.current){
            //     case 0:
            //         break;
            //     case 1:
            //         _tube.trajectory = (t:number) => {return[2.0*Math.cos(t*3.0),2.0*Math.sin(t*3.0),0.0];}
            //         break;
            //     default:
            //         _tube.trajectory = (t:number) => {return[4.0*Math.cos(t)*Math.sin(t)+2.0*Math.cos(t*10.0),4.0*Math.sin(t),2.0*Math.sin(t*10.0)];}
            //         break;
            // }
            _tube.trajectory = (t:number) => {return func(from,to,trans,t);}
            for(let i = 0; i < tubeCount; i++){
                let pos = func(from,to,trans,t + tubeTimeOffset[i]);
                pos[0] += tubePosOffset[i * 3];
                pos[1] += tubePosOffset[i * 3+1];
                pos[2] += tubePosOffset[i * 3+2];
                instancedTubeA.AddPosition(pos,i);
            }
            for(let i = 0; i < tubeCount; i++){
                let pos = func(from,to,trans,t + tubeTimeOffset[i + tubeCount]);
                pos[0] += tubePosOffset[(i + tubeCount) * 3];
                pos[1] += tubePosOffset[(i + tubeCount) * 3+1];
                pos[2] += tubePosOffset[(i + tubeCount) * 3+2];
                instancedTubeB.AddPosition(pos,i);
            }
            for(let i = 0; i < tubeCount; i++){
                let pos = func(from,to,trans,t + tubeTimeOffset[i + tubeCount*2]);
                pos[0] += tubePosOffset[(i + tubeCount*2) * 3];
                pos[1] += tubePosOffset[(i + tubeCount*2) * 3+1];
                pos[2] += tubePosOffset[(i + tubeCount*2) * 3+2];
                instancedTubeC.AddPosition(pos,i);
            }
            instancedTubeA.UpdateBuffer(gl);
            instancedTubeB.UpdateBuffer(gl);
            instancedTubeC.UpdateBuffer(gl);

            instancedTubeA.uniforms[1].val = [r-0.01,r,r];
            instancedTubeB.uniforms[1].val = [r-0.01,r,r];
            instancedTubeC.uniforms[1].val = [r-0.01,r,r];

            instancedDonuts.Draw(camera,gl,ext);
            instancedCrosses.Draw(camera,gl,ext);
            instancedDonutsShadow.Draw(camera,gl,ext);
            instancedCrossesShadow.Draw(camera,gl,ext);
            instancedTubeA.Draw(camera,gl,ext);
            instancedTubeB.Draw(camera,gl,ext);
            instancedTubeC.Draw(camera,gl,ext);

            requestAnimationFrame(render);
        };
        render();
        gl.flush();


    },[canvasRef]);
    return(
        <canvas height={1000} width={2000} style={{position:"absolute",height:"100%",width:"100%"}} ref={canvasRef}/>
    );
};

export default BGCanvas;